
import React, { useState, useEffect, useRef } from 'react';
import { agreementService } from '../../services/api';
import Navbar from '../Dashboard/components/Navbar';
import Footer from '../Dashboard/components/Footer';

interface ClauseAmendment {
    id: string;
    clauseName: string;
    issue: string;
    severity: 'High' | 'Medium' | 'Low';
    originalText: string;
    suggestedText: string;
    regulationRef?: string;
}

interface Agreement {
    id: string;
    title: string;
    currentVersion: number;
    status: string;
    createdAt: string;
    updatedAt: string;
    complianceScore?: number;
    amendments?: ClauseAmendment[];
}

const TenancyAgreementManager: React.FC = () => {
    const [agreements, setAgreements] = useState<Agreement[]>([]);
    const [loading, setLoading] = useState(true);
    const [activeTab, setActiveTab] = useState<'MY_AGREEMENTS' | 'UPLOAD_NEW' | 'ANALYSIS'>('MY_AGREEMENTS');
    const [selectedAgreement, setSelectedAgreement] = useState<Agreement | null>(null);
    const [selectedAmendments, setSelectedAmendments] = useState<string[]>([]);
    const [uploading, setUploading] = useState(false);
    const fileInputRef = useRef<HTMLInputElement>(null);

    const [extractedText, setExtractedText] = useState<string | null>(null);

    useEffect(() => {
        fetchAgreements();
    }, []);

    const fetchAgreements = async () => {
        setLoading(true);
        try {
            const res = await agreementService.list();
            setAgreements(res.data.data || []);
        } catch (err) {
            console.error('Failed to fetch agreements', err);
        } finally {
            setLoading(false);
        }
    };

    const handleFileUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
        const file = e.target.files?.[0];
        if (!file) return;

        setUploading(true);

        const toBase64 = (file: File): Promise<string> => {
            return new Promise((resolve, reject) => {
                const reader = new FileReader();
                reader.readAsDataURL(file);
                reader.onload = () => {
                    const result = reader.result as string;
                    resolve(result.split(',')[1]);
                };
                reader.onerror = error => reject(error);
            });
        };

        try {
            const base64Data = await toBase64(file);

            // 1. Extract text
            const extractRes = await agreementService.extractText({
                file: base64Data,
                fileName: file.name,
                mimeType: file.type
            });
            const { text, fileName } = extractRes.data.data;
            setExtractedText(text);

            // 2. Create agreement record
            const createRes = await agreementService.upload({
                title: fileName,
                text: text
            });

            const newAgreement = createRes.data.data;

            // 3. Trigger Analysis automatically
            const analysisRes = await agreementService.analyze(newAgreement.id, {});
            const fullAgreement = { ...newAgreement, ...analysisRes.data.data };

            setSelectedAgreement(fullAgreement);
            setActiveTab('ANALYSIS');
            await fetchAgreements();
        } catch (err: any) {
            console.error('Upload failed details:', err);
            const serverMsg = err.response?.data?.message || err.response?.data?.error || err.message || 'Unknown error';
            alert(`Failed to process document: ${serverMsg}`);
        } finally {
            setUploading(false);
            if (fileInputRef.current) fileInputRef.current.value = '';
        }
    };

    const toggleAmendment = (id: string) => {
        setSelectedAmendments(prev =>
            prev.includes(id) ? prev.filter(a => a !== id) : [...prev, id]
        );
    };

    const selectAllAmendments = () => {
        if (!selectedAgreement?.amendments) return;
        setSelectedAmendments(selectedAgreement.amendments.map(a => a.id));
    };

    const clearAmendments = () => {
        setSelectedAmendments([]);
    };

    const handleApplyAmendments = async () => {
        if (!selectedAgreement) return;

        // Fully-compliant path: nothing to amend — the agreement was already
        // persisted at upload time, so "Done" just confirms the review and
        // returns to the list. No POST needed.
        if (selectedAmendments.length === 0) {
            await fetchAgreements();
            setSelectedAgreement(null);
            setActiveTab('MY_AGREEMENTS');
            return;
        }

        setLoading(true);
        try {
            await agreementService.amend(selectedAgreement.id, {
                amendmentIds: selectedAmendments
            });
            alert('Amendments applied successfully!');
            await fetchAgreements();
            setActiveTab('MY_AGREEMENTS');
        } catch (err) {
            console.error('Failed to apply amendments', err);
            alert('Error applying amendments.');
        } finally {
            setLoading(false);
        }
    };

    const handleViewReport = async (ag: Agreement) => {
        setLoading(true);
        try {
            const [analysisRes, textRes] = await Promise.all([
                agreementService.analyze(ag.id, {}),
                agreementService.get(ag.id)
            ]);
            const analysisData = analysisRes.data?.data || {};
            const agreementText = textRes.data?.data?.extractedText || textRes.data?.data?.text || '';
            setSelectedAgreement({ ...ag, ...analysisData, extractedText: agreementText });
            setActiveTab('ANALYSIS');
        } catch (err: any) {
            const serverMsg = err?.response?.data?.message || err?.response?.data?.error || err?.message || 'Failed to load report';
            alert(`Error: ${serverMsg}`);
            console.error('Failed to get report', err);
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className="min-h-screen bg-gray-50 flex flex-col font-sans">
            <Navbar />

            <main className="flex-1 max-w-5xl w-full mx-auto px-4 py-8">
                {/* Header Section */}
                <div className="mb-8">
                    <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
                        <i className="fas fa-file-alt text-gray-700"></i>
                        Tenancy Agreement Manager
                    </h1>
                    <p className="text-gray-500 text-sm mt-1">
                        Upload, analyze, and auto-amend your tenancy agreements for UK compliance
                    </p>
                </div>

                {/* Tabs */}
                <div className="flex border-b border-gray-200 mb-8 mt-4">
                    <button
                        onClick={() => setActiveTab('MY_AGREEMENTS')}
                        className={`px-6 py-2 text-sm font-medium border-b-2 transition-all ${activeTab === 'MY_AGREEMENTS' ? 'border-green-600 text-green-600' : 'border-transparent text-gray-500 hover:text-gray-700'}`}
                    >
                        My Agreements
                    </button>
                    <button
                        onClick={() => setActiveTab('UPLOAD_NEW')}
                        className={`px-6 py-2 text-sm font-medium border-b-2 transition-all ${activeTab === 'UPLOAD_NEW' ? 'border-green-600 text-green-600' : 'border-transparent text-gray-500 hover:text-gray-700'}`}
                    >
                        Upload New
                    </button>
                    {(selectedAgreement || activeTab === 'ANALYSIS') && (
                        <button
                            onClick={() => setActiveTab('ANALYSIS')}
                            className={`px-6 py-2 text-sm font-medium border-b-2 transition-all ${activeTab === 'ANALYSIS' ? 'border-green-600 text-green-600' : 'border-transparent text-gray-500 hover:text-gray-700'}`}
                        >
                            Analysis Results
                        </button>
                    )}
                </div>

                {loading ? (
                    <div className="flex flex-col items-center justify-center py-20 text-gray-400">
                        <i className="fas fa-circle-notch fa-spin text-3xl mb-4"></i>
                        <p>Processing...</p>
                    </div>
                ) : activeTab === 'MY_AGREEMENTS' ? (
                    <div className="space-y-4">
                        {agreements.length > 0 ? (
                            agreements.map(ag => (
                                <div key={ag.id} className="bg-white border rounded-xl p-6 flex items-center justify-between shadow-sm hover:shadow-md transition-all">
                                    <div className="flex items-center gap-4">
                                        <div className="w-12 h-12 bg-gray-100 rounded-lg flex items-center justify-center text-gray-500">
                                            <i className="fas fa-file-contract text-xl"></i>
                                        </div>
                                        <div>
                                            <h3 className="font-bold text-gray-900">{ag.title}</h3>
                                            <p className="text-xs text-gray-500">
                                                Version {ag.currentVersion} • Updated {new Date(ag.updatedAt).toLocaleDateString()}
                                            </p>
                                        </div>
                                    </div>
                                    <div className="flex gap-3">
                                        <button
                                            onClick={() => handleViewReport(ag)}
                                            className="text-sm font-bold text-green-600 hover:text-green-700 flex items-center gap-2"
                                        >
                                            VIEW REPORT <i className="fas fa-chevron-right text-xs"></i>
                                        </button>
                                        <button
                                            onClick={async (e) => {
                                                e.stopPropagation();
                                                if (!window.confirm('Delete this agreement?')) return;
                                                try {
                                                    await agreementService.delete(ag.id);
                                                    fetchAgreements();
                                                } catch (err) {
                                                    alert('Delete failed.');
                                                }
                                            }}
                                            className="text-sm text-red-500 hover:text-red-700 p-2 rounded-lg hover:bg-red-50 transition"
                                            title="Delete Agreement"
                                        >
                                            <i className="fas fa-trash"></i>
                                        </button>
                                    </div>
                                </div>
                            ))
                        ) : (
                            <div className="text-center py-20 bg-white border-2 border-dashed rounded-3xl">
                                <i className="fas fa-file-upload text-gray-200 text-5xl mb-4"></i>
                                <h3 className="text-lg font-bold text-gray-900">No agreements uploaded</h3>
                                <p className="text-gray-500 text-sm mt-2">Upload your first agreement to run compliance analysis.</p>
                                <button
                                    onClick={() => setActiveTab('UPLOAD_NEW')}
                                    className="mt-6 bg-green-600 text-white px-6 py-2 rounded-lg font-bold hover:bg-green-700 transition"
                                >
                                    UPLOAD NOW
                                </button>
                            </div>
                        )}
                    </div>
                ) : activeTab === 'UPLOAD_NEW' ? (
                    <div className="bg-white border-2 border-dashed rounded-3xl p-12 text-center">
                        <div className="w-20 h-20 bg-green-50 rounded-full flex items-center justify-center mx-auto mb-6">
                            <i className="fas fa-cloud-upload-alt text-green-600 text-3xl"></i>
                        </div>
                        <h2 className="text-xl font-bold text-gray-900 mb-2">Upload Tenancy Agreement</h2>
                        <p className="text-gray-500 mb-8 max-w-sm mx-auto text-sm">
                            Drag and drop your file here, or click to browse.
                            Specifically supported formats:
                        </p>

                        <div className="flex justify-center gap-4 mb-10">
                            {['.PDF', '.DOCX', '.TXT'].map(ext => (
                                <span key={ext} className="px-4 py-1.5 bg-gray-100 rounded-md text-xs font-bold text-gray-600 border">
                                    {ext}
                                </span>
                            ))}
                        </div>

                        <input
                            type="file"
                            ref={fileInputRef}
                            className="hidden"
                            onChange={handleFileUpload}
                            accept=".pdf,.docx,.txt"
                        />
                        <button
                            onClick={() => fileInputRef.current?.click()}
                            disabled={uploading}
                            className={`bg-green-600 text-white px-8 py-3 rounded-xl font-bold hover:bg-green-700 transition shadow-lg ${uploading ? 'opacity-50' : ''}`}
                        >
                            {uploading ? 'EXTRACTING TEXT...' : 'SELECT FILE'}
                        </button>
                    </div>
                ) : selectedAgreement && (
                    <div className="space-y-8 animate-in fade-in duration-500">
                        <div className="flex justify-between items-start">
                            <h2 className="text-xl font-bold text-gray-800">
                                Compliance Analysis: {selectedAgreement.title}
                            </h2>
                            {(() => {
                                // Plain Compliant / Not Compliant pill. The %
                                // score the analyser returns has been unreliable
                                // (was rendering 290%, 1000%, 50%, etc. — the
                                // server is producing aggregate weights that
                                // exceed 100). Until that's stabilised we render
                                // boolean compliance based on whether any
                                // amendments came back.
                                const ams = selectedAgreement.amendments || [];
                                const isCompliant = ams.length === 0;
                                return isCompliant ? (
                                    <div className="bg-emerald-600 text-white px-5 py-2.5 rounded-lg shadow-sm flex items-center gap-2">
                                        <i className="fas fa-check-circle"></i>
                                        <span className="text-xs uppercase font-bold tracking-widest">Compliant</span>
                                    </div>
                                ) : (
                                    <div className="bg-[#f87171] text-white px-5 py-2.5 rounded-lg shadow-sm flex items-center gap-2">
                                        <i className="fas fa-exclamation-triangle"></i>
                                        <span className="text-xs uppercase font-bold tracking-widest">Not Compliant</span>
                                    </div>
                                );
                            })()}
                        </div>

                        {/* Extracted Text Section */}
                        <div className="bg-white border rounded-xl overflow-hidden shadow-sm">
                            <div className="px-5 py-3 bg-gray-50 border-b flex justify-between items-center">
                                <h3 className="text-sm font-bold text-gray-700 uppercase tracking-wider">Extracted Text Preview</h3>
                                <button
                                    onClick={() => setSelectedAgreement(null)}
                                    className="text-xs text-gray-500 hover:text-gray-700"
                                >
                                    <i className="fas fa-times"></i>
                                </button>
                            </div>
                            <div className="p-5 max-h-60 overflow-y-auto bg-gray-50/50">
                                <pre className="text-[11px] text-gray-600 whitespace-pre-wrap font-mono leading-relaxed">
                                    {extractedText || (selectedAgreement as any).text || "No text content available for this report."}
                                </pre>
                            </div>
                        </div>

                        {/* Amendment Report Summary — only show when issues exist.
                            No percentages: the analyser's score has been
                            inflating past 100% so we surface the boolean
                            verdict + the actual issue list instead. */}
                        {(selectedAgreement.amendments?.length || 0) > 0 ? (
                            <div className="bg-white border-l-4 border-l-indigo-500 rounded-xl p-5 shadow-sm">
                                <p className="text-sm text-gray-600 leading-relaxed">
                                    <span className="font-bold text-gray-800">Amendment Report:</span> {selectedAgreement.amendments?.length || 0} issue(s) found.
                                    <span className="text-red-600 font-bold ml-1">🔴 CRITICAL ({selectedAgreement.amendments?.filter(a => a.severity === 'High').length || 0}):</span> These should be fixed to meet RRA 2025 requirements.
                                    {selectedAgreement.amendments?.map((a, i) => (
                                        <span key={a.id} className="ml-1">
                                            {i + 1}. {a.issue.split('.')[0]}
                                            {i < (selectedAgreement.amendments?.length || 0) - 1 ? ' ' : ''}
                                        </span>
                                    ))}
                                </p>
                            </div>
                        ) : (
                            <div className="bg-emerald-50 border-l-4 border-l-emerald-500 rounded-xl p-5 shadow-sm">
                                <p className="text-sm text-emerald-800 leading-relaxed">
                                    <span className="font-bold">Fully compliant.</span> The analyser found no clauses that require amendment.
                                    The agreement meets RRA 2025 and standard UK tenancy requirements. Click <span className="font-bold">Done</span> below to save it to your library.
                                </p>
                            </div>
                        )}

                        {/* Amendments Container */}
                        <div className="space-y-6">
                            <div className="flex justify-between items-center">
                                <h3 className="font-bold text-gray-900">Amendments Required ({selectedAgreement.amendments?.length || 0})</h3>
                                <div className="space-x-4">
                                    <button onClick={selectAllAmendments} className="text-xs font-bold text-gray-500 hover:text-green-600 uppercase">Select All</button>
                                    <button onClick={clearAmendments} className="text-xs font-bold text-gray-500 hover:text-red-500 uppercase">Clear</button>
                                </div>
                            </div>

                            {selectedAgreement.amendments?.map(amendment => (
                                <div key={amendment.id} className="bg-white border rounded-xl overflow-hidden shadow-sm">
                                    <div className="p-5 flex gap-4">
                                        <input
                                            type="checkbox"
                                            className="mt-1 w-5 h-5 accent-green-600 cursor-pointer"
                                            checked={selectedAmendments.includes(amendment.id)}
                                            onChange={() => toggleAmendment(amendment.id)}
                                        />
                                        <div className="flex-1">
                                            <div className="flex items-center gap-2 mb-2">
                                                <span className="bg-red-600 text-white text-[10px] font-black px-2 py-0.5 rounded uppercase tracking-tighter">CRITICAL</span>
                                                <span className="text-sm font-bold text-gray-800">{amendment.clauseName}</span>
                                                <span className="bg-indigo-50 text-indigo-600 text-[10px] font-bold px-2 py-0.5 rounded">{amendment.regulationRef || 'RRA 2025'}</span>
                                            </div>
                                            <p className="text-sm text-gray-500 mb-4">{amendment.issue}</p>

                                            <div className="space-y-3">
                                                <div className="bg-[#fef2f2] border border-red-100 rounded-lg p-4">
                                                    <h5 className="text-[10px] font-black text-red-600 uppercase mb-2">Before:</h5>
                                                    <p className="text-sm text-gray-700 font-medium italic">"{amendment.originalText}"</p>
                                                </div>
                                                <div className="bg-[#f0fdf4] border border-green-100 rounded-lg p-4">
                                                    <h5 className="text-[10px] font-black text-green-600 uppercase mb-2">After:</h5>
                                                    <p className="text-sm text-gray-800 font-bold">{amendment.suggestedText}</p>
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            ))}
                        </div>

                        {/* Apply / Done button — when the agreement is fully
                            compliant (0 amendments) the button becomes a
                            clickable "Done" that returns to the library. */}
                        {(() => {
                            // Button rule:
                            //   nothing selected  -> "Done" (emerald, saves
                            //     and returns to library — no POST needed)
                            //   N selected        -> "Apply N Amendment(s)"
                            //     (indigo, posts to the amend endpoint)
                            // Always clickable so the officer can exit
                            // without applying changes.
                            const noneSelected = selectedAmendments.length === 0;
                            const label  = noneSelected
                                ? 'Done'
                                : `Apply ${selectedAmendments.length} Amendment(s)`;
                            const colour = noneSelected
                                ? 'bg-emerald-600 hover:bg-emerald-700 active:scale-[0.98]'
                                : 'bg-indigo-500 hover:bg-indigo-600 active:scale-[0.98]';
                            return (
                                <button
                                    onClick={handleApplyAmendments}
                                    disabled={loading}
                                    className={`w-full py-4 rounded-xl text-white font-black text-lg shadow-xl transition-all ${colour}`}
                                >
                                    {label}
                                </button>
                            );
                        })()}

                        {/* Recommendations */}
                        <div className="bg-white border rounded-3xl p-8">
                            <h4 className="font-bold text-gray-900 mb-4 uppercase tracking-wider text-sm">Recommendations</h4>
                            <ul className="space-y-3">
                                {[
                                    'Have amendments reviewed by a qualified solicitor before use',
                                    'Ensure all pre-tenancy documents (Gas Safety, EPC, How to Rent) are served',
                                    'Register deposit in a government-approved scheme within 30 days'
                                ].map((rec, i) => (
                                    <li key={i} className="flex gap-3 text-sm text-gray-600 font-medium">
                                        <i className="fas fa-check-circle text-green-500 mt-0.5"></i>
                                        {rec}
                                    </li>
                                ))}
                            </ul>
                        </div>
                    </div>
                )}
            </main>
            <Footer />
        </div>
    );
};

export default TenancyAgreementManager;
